import React, { useState } from "react";
import {
  WifiIcon,
  ServerIcon,
  GlobeAltIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  ChartBarIcon,
  SignalIcon,
  EyeIcon,
  Cog6ToothIcon,
  PhoneIcon,
  XMarkIcon,
  MapPinIcon
} from "@heroicons/react/24/outline";

const PackageList = ({ 
  packages = [], 
  isDarkMode, 
  getStatusBadge, 
  getTypeIcon, 
  formatCurrency, 
  formatDate,
  onDetailClick
}) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Helper format date - fallback if not provided from parent
  const formatDateLocal = formatDate || ((dateStr) => {
    if (!dateStr) return 'Tidak tersedia';
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  });

  // Helper format currency - fallback if not provided from parent
  const formatCurrencyLocal = formatCurrency || ((num) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(num);
  });

  // Get type icon - fallback if not provided from parent
  const getTypeIconLocal = getTypeIcon || ((type) => {
    switch (type) {
      case "broadband":
        return <WifiIcon className="h-6 w-6 text-blue-500" />;
      case "dedicated":
        return <ServerIcon className="h-6 w-6 text-green-500" />;
      case "satelit":
        return <GlobeAltIcon className="h-6 w-6 text-purple-500" />;
      default:
        return <WifiIcon className="h-6 w-6 text-gray-400" />;
    }
  });

  // Get status badge - fallback if not provided from parent
  const getStatusBadgeLocal = getStatusBadge || ((status) => {
    const base = "px-2 py-1 text-xs font-semibold rounded-full";
    if (status === "aktif") return <span className={`${base} bg-green-100 text-green-700`}>Aktif</span>;
    if (status === "pending") return <span className={`${base} bg-yellow-100 text-yellow-700`}>Pending</span>;
    if (status === "suspend") return <span className={`${base} bg-red-100 text-red-700`}>Suspend</span>;
    if (status === "expired") return <span className={`${base} bg-gray-100 text-gray-700`}>Expired</span>;
    return <span className={`${base} bg-gray-100 text-gray-700`}>{status}</span>;
  });

  // Handle opening detail modal
  const handleShowDetail = (pkg) => {
    if (onDetailClick) {
      onDetailClick(pkg);
    } else {
      setSelectedPackage(pkg);
      setShowDetailModal(true);
    }
  };

  // Handle closing detail modal
  const handleCloseDetail = () => {
    setSelectedPackage(null);
    setShowDetailModal(false);
  };

  // Format timeline for display
  const formatTimeline = (timeline) => {
    if (!timeline || !Array.isArray(timeline)) return [];
    return timeline.map(item => ({
      ...item,
      formattedDate: formatDateLocal(item.timestamp || item.date || item.createdAt)
    }));
  };

  return (
    <>
      {/* Detail Modal */}
      {showDetailModal && selectedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[2001] flex items-center justify-center p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            {/* Modal Header */}
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <div className="flex items-center space-x-3">
                {getTypeIconLocal(selectedPackage.type)}
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedPackage.name}
                  </h2>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Order: {selectedPackage.orderNumber || selectedPackage.id}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseDetail}
                className={`p-2 rounded-lg hover:bg-gray-100 ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'text-gray-500'}`}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Package Info */}
                <div className="space-y-6">
                  {/* Status & Basic Info */}
                  <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                    <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Informasi Paket
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status</span>
                        {getStatusBadgeLocal(selectedPackage.status)}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Kecepatan</span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedPackage.speed}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tipe</span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedPackage.type?.charAt(0).toUpperCase() + selectedPackage.type?.slice(1) || 'Unknown'}
                        </span>
                      </div>
                      {selectedPackage.uptime !== undefined && (
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Uptime</span>
                          <span className={`text-sm font-medium ${selectedPackage.uptime >= 99 ? 'text-green-600' : selectedPackage.uptime >= 95 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {selectedPackage.uptime}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Billing Info */}
                  <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                    <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Informasi Tagihan
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Biaya Bulanan</span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrencyLocal(selectedPackage.monthlyFee)}
                        </span>
                      </div>
                      {selectedPackage.lastPayment && (
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pembayaran Terakhir</span>
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {formatDateLocal(selectedPackage.lastPayment)}
                          </span>
                        </div>
                      )}
                      {selectedPackage.nextPayment && (
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Pembayaran Selanjutnya</span>
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {formatDateLocal(selectedPackage.nextPayment)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location */}
                  {selectedPackage.location && (
                    <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Lokasi Pemasangan
                      </h3>
                      <div className="flex items-start space-x-3">
                        <MapPinIcon className={`h-5 w-5 mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {selectedPackage.location}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Usage & Timeline */}
                <div className="space-y-6">
                  {/* Data Usage */}
                  {selectedPackage.dataUsage && (
                    <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Penggunaan Data
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Digunakan</span>
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {selectedPackage.dataUsage.used} GB
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total</span>
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {selectedPackage.dataUsage.total}
                          </span>
                        </div>
                        <div className={`w-full bg-gray-200 rounded-full h-2 ${isDarkMode ? 'bg-gray-600' : ''}`}>
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{
                              width: selectedPackage.dataUsage.total === "Unlimited" 
                                ? '25%' 
                                : `${Math.min((selectedPackage.dataUsage.used / parseInt(selectedPackage.dataUsage.total)) * 100, 100)}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Service Period */}
                  <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                    <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Periode Layanan
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Mulai</span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {formatDateLocal(selectedPackage.startDate)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Berakhir</span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {formatDateLocal(selectedPackage.endDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  {selectedPackage.timeline && selectedPackage.timeline.length > 0 && (
                    <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Riwayat Status
                      </h3>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {formatTimeline(selectedPackage.timeline).map((item, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full mt-2 ${
                              item.status === 'active' || item.status === 'installed' 
                                ? 'bg-green-500' 
                                : item.status === 'pending' 
                                ? 'bg-yellow-500'
                                : 'bg-gray-500'
                            }`}></div>
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {item.status || item.action || 'Status Update'}
                              </p>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {item.formattedDate}
                              </p>
                              {item.description && (
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Order Data if available */}
                  {selectedPackage.orderData && (
                    <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Informasi Order
                      </h3>
                      <div className="space-y-3">
                        {selectedPackage.orderData.schedule?.activationDate && (
                          <div className="flex justify-between items-center">
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tanggal Aktivasi</span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {formatDateLocal(selectedPackage.orderData.schedule.activationDate)}
                            </span>
                          </div>
                        )}
                        {selectedPackage.orderData.customer && (
                          <div className="flex justify-between items-center">
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Customer</span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedPackage.orderData.customer.name || selectedPackage.orderData.customer.phone}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedPackage.description && (
                <div className={`mt-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                  <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Deskripsi Paket
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {selectedPackage.description}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={handleCloseDetail}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  Tutup
                </button>
                {selectedPackage.status === "aktif" && (
                  <button className="px-4 py-2 bg-[#1E9B4C] hover:bg-green-700 text-white rounded-lg font-medium transition-colors">
                    Kelola Paket
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Package Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {packages.map(pkg => (
          <div
            key={pkg.id}
            className={`${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                {getTypeIconLocal(pkg.type)}
                <div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{pkg.name}</h3>
                  <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{pkg.speed} • ID: {pkg.id}</p>
                </div>
              </div>
              {getStatusBadgeLocal(pkg.status)}
            </div>

            {/* Description */}
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-4`}>{pkg.description}</p>

            {/* Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Periode Aktif */}
              <div className={`${isDarkMode ? "bg-gray-700" : "bg-gray-50"} rounded-lg p-3`}>
                <div className="flex items-center space-x-2 mb-1">
                  <CalendarDaysIcon className={`h-4 w-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Periode Aktif</span>
                </div>
                <p className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  {formatDateLocal(pkg.startDate)} - {formatDateLocal(pkg.endDate)}
                </p>
              </div>

              {/* Biaya Bulanan */}
              <div className={`${isDarkMode ? "bg-gray-700" : "bg-gray-50"} rounded-lg p-3`}>
                <div className="flex items-center space-x-2 mb-1">
                  <CreditCardIcon className={`h-4 w-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Biaya Bulanan</span>
                </div>
                <p className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  {formatCurrencyLocal(pkg.monthlyFee)}
                </p>
              </div>

              {/* Penggunaan Data */}
              <div className={`${isDarkMode ? "bg-gray-700" : "bg-gray-50"} rounded-lg p-3`}>
                <div className="flex items-center space-x-2 mb-1">
                  <ChartBarIcon className={`h-4 w-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Penggunaan Data</span>
                </div>
                <p className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>{pkg.dataUsage.used} GB / {pkg.dataUsage.total}</p>
              </div>

              {/* Uptime */}
              <div className={`${isDarkMode ? "bg-gray-700" : "bg-gray-50"} rounded-lg p-3`}>
                <div className="flex items-center space-x-2 mb-1">
                  <SignalIcon className={`h-4 w-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Uptime</span>
                </div>
                <p className={`text-sm font-medium ${pkg.uptime >= 99 ? "text-green-600" : pkg.uptime >= 95 ? "text-yellow-600" : "text-red-600"}`}>
                  {pkg.uptime}%
                </p>
              </div>
            </div>

            {/* Lokasi */}
            <div className={`${isDarkMode ? "bg-gray-700" : "bg-gray-50"} rounded-lg p-3 mb-6`}>
              <div className="flex items-center space-x-2 mb-1">
                <svg className={`h-4 w-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Lokasi Pemasangan</span>
              </div>
              <p className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>{pkg.location}</p>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => handleShowDetail(pkg)}
                className="flex items-center space-x-2 px-4 py-2 bg-[#1E9B4C] hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-sm"
              >
                <EyeIcon className="h-4 w-4" />
                <span>Detail</span>
              </button>

              {pkg.status === "aktif" && (
                <>
                  <button className={`${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-700"} flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm`}>
                    <Cog6ToothIcon className="h-4 w-4" />
                    <span>Kelola</span>
                  </button>
                  <button className={`${isDarkMode ? "bg-blue-800 hover:bg-blue-700 text-blue-200" : "bg-blue-100 hover:bg-blue-200 text-blue-700"} flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm`}>
                    <PhoneIcon className="h-4 w-4" />
                    <span>Support</span>
                  </button>
                </>
              )}

              {pkg.status === "suspend" && (
                <button className={`${isDarkMode ? "bg-yellow-800 hover:bg-yellow-700 text-yellow-200" : "bg-yellow-100 hover:bg-yellow-200 text-yellow-700"} flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm`}>
                  <CreditCardIcon className="h-4 w-4" />
                  <span>Bayar Tagihan</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default PackageList;